import type { CardFormat } from '@/features/dashboard/api/schemas'
import { changeOf } from '@/features/dashboard/format/figure'
import {
  formatAbbrev,
  formatChangeIn,
  formatValue,
  toldByArrow,
  type ChangeUnit,
} from '@/features/dashboard/format/format'
import type { ChartPalette } from './chartTheme'
import styles from '../ExpandedBarChart.module.css'

const TRIANGLE_UP =
  'M17.8892 15.7522C18.2722 16.4188 17.7909 17.2503 17.0221 17.2503H6.97756C6.20872 17.2503 5.72748 16.4188 6.11045 15.7522L11.1327 7.00969C11.5171 6.34052 12.4825 6.34052 12.8669 7.00969L17.8892 15.7522Z'
const TRIANGLE_DOWN =
  'M17.8892 8.24812C18.2722 7.58146 17.7909 6.75 17.0221 6.75H6.97756C6.20872 6.75 5.72748 7.58146 6.11045 8.24813L11.1327 16.9906C11.5171 17.6598 12.4825 17.6598 12.8669 16.9906L17.8892 8.24812Z'

const escapeHtml = (text: string) =>
  text.replace(
    /[&<>"']/g,
    char =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        char
      ] ?? char
  )

type TooltipContent = {
  label: string
  value: number
  previous: number | null
  unit: string | null | undefined
  palette: ChartPalette
  /** A fall is good news: the change's colours swap. */
  lowerIsBetter?: boolean
  /** How the change reads, as on the card's badge: relative %, or a difference in ppt / pts. */
  changeUnit?: ChangeUnit
  /** What the change compares ('MoM', 'QoQ', 'YoY'), shown after it. */
  changeLabel?: string | undefined
  /** Percent cards hold fractions, shown as percentages. */
  format?: CardFormat | undefined
  /** Benchmarks at the same category (target, budget...), listed under the change. */
  references?: {
    label: string
    value: number
    color: string
    /** A bar's value rather than a line's: a filled square, not a ring. */
    square?: boolean
  }[]
}

/** A chart value as the card's format reads: a percentage, else abbreviated (€1.50M). */
export const formatChartValue = (
  value: number,
  unit: string | null | undefined,
  format: CardFormat | undefined,
  decimals = 2
): string =>
  format?.style === 'percent'
    ? formatValue(value, { format })
    : formatAbbrev(value, unit, decimals)

export const tooltipHtml = ({
  label,
  value,
  previous,
  unit,
  palette,
  lowerIsBetter = false,
  changeUnit = 'relative',
  changeLabel,
  format,
  references = [],
}: TooltipContent) => {
  const result =
    previous === null
      ? null
      : changeOf(
          value,
          previous,
          format ?? { style: 'number', decimals: null },
          changeUnit
        )
  const change =
    result === null
      ? ''
      : (() => {
          const up = value >= (previous ?? 0)
          // A relative change is told by the arrow; a difference keeps its sign.
          const text = formatChangeIn(result.amount, result.unit, {
            unit,
            format,
          })
          return (
            `<div class="${styles.tooltipChange}" style="color:${up !== lowerIsBetter ? palette.positive : palette.negative}">` +
            `<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="${up ? TRIANGLE_UP : TRIANGLE_DOWN}"/></svg>` +
            `<span>${escapeHtml(toldByArrow(result.unit) ? text.replace(/^[+−]/, '') : text)}</span>` +
            (changeLabel
              ? `<span class="${styles.tooltipChangeLabel}" style="color:${palette.textAlt}">${escapeHtml(changeLabel)}</span>`
              : '') +
            '</div>'
          )
        })()
  const referenceRows = references
    .map(
      reference =>
        `<span class="${styles.tooltipRow}" style="color:${palette.textAlt}">` +
        `<span class="${styles.tooltipRing}" style="border-color:${reference.color}${reference.square ? `;background:${reference.color};border-radius:2px` : ''}"></span>` +
        `${escapeHtml(reference.label)}: ${escapeHtml(formatChartValue(reference.value, unit, format))}</span>`
    )
    .join('')
  return (
    `<div class="${styles.tooltip}" style="background:${palette.card};border-color:${palette.crosshair};color:${palette.text}">` +
    `<span class="${styles.tooltipLabel}" style="color:${palette.textAlt}">${escapeHtml(label)}</span>` +
    `<span class="${styles.tooltipValue}">${escapeHtml(formatChartValue(value, unit, format))}</span>` +
    `${change}${referenceRows}</div>`
  )
}

type SeriesRow = {
  /** Left out for a lone series, whose label would only repeat the card's. */
  label?: string
  value: number
  color: string
  /** A benchmark: its swatch is a dashed line rather than a dot. */
  dashed?: boolean
}

/** A multi-series tooltip: the category, then a coloured row per series. */
export const seriesTooltipHtml = ({
  label,
  rows,
  palette,
  unit,
  format,
}: {
  label: string
  rows: SeriesRow[]
  palette: ChartPalette
  unit: string | null | undefined
  format: CardFormat | undefined
}) =>
  `<div class="${styles.tooltip}" style="background:${palette.card};border-color:${palette.crosshair};color:${palette.text}">` +
  `<span class="${styles.tooltipLabel}" style="color:${palette.textAlt}">${escapeHtml(label)}</span>` +
  rows
    .map(
      row =>
        `<span class="${styles.tooltipRow}">` +
        (row.dashed
          ? `<span class="${styles.tooltipSwatch}" style="border-color:${row.color}"></span>`
          : `<span class="${styles.tooltipDot}" style="background:${row.color}"></span>`) +
        `<span>${row.label === undefined ? '' : `${escapeHtml(row.label)}: `}${escapeHtml(formatChartValue(row.value, unit, format))}</span></span>`
    )
    .join('') +
  '</div>'
